'use client'
import { useCallback, useMemo, useState, useEffect, memo } from 'react'
import type { ChangeEvent, JSX, ReactNode } from 'react'
import type { UseFormRegister as TypeRegister, UseFormSetValue, FieldError, RegisterOptions } from 'react-hook-form'
import { motion } from 'motion/react'
import styled, { css } from 'styled-components'
import { mq, Col, parsePlaceHolder, getTransition, getP18InputLabel, getP18InputHasContentLabel, getP18, vw } from '@/styles'
import { MODALS } from '@/context'
import { useDropdown } from '../dropdown/useDropdown'
import { Img } from '../Img'
import {  useDeviceType, useModal } from '@/hooks'
import { Span, transition, TypeThemeColors, variantsSpan } from './Input'

const noRegister = () => ({ onChange: null, onBlur: null, ref: null, name: null })

type onChangeType = (event: ChangeEvent<HTMLInputElement>) => void
export type TypeOption = {
  value: string
  name: string
  [key:string]:string
}
interface iSelect {
  options: TypeOption[]
  readonly name: string
  readonly label?: string
  placeholder?: string
  defaultValue?: string | number
  readonly required?: boolean | RegisterOptions
  readonly disabled?: boolean
  readonly autoComplete?: string
  readonly readOnly?: boolean
  readonly onChange?: onChangeType
  readonly col?: Col
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly register?:TypeRegister<any>
  error?: FieldError | unknown
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFormValue?: UseFormSetValue<any>
  readonly icon?: string | boolean | ReactNode
  readonly className?: string
  readonly placeholderVisible?: boolean
  readonly textColor?: TypeThemeColors
  readonly dropdownTextColor?: TypeThemeColors
  readonly bgColor?: TypeThemeColors
  modalId?: MODALS
  reset?: boolean
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
  setReset?: any
  searchDisabled?: boolean,
  $light?: boolean
}

interface iLabelProps {
  $hasContent: boolean
  $placeholderVisible: boolean
  $textColor?: TypeThemeColors
  $bgColor: TypeThemeColors
}

interface iDivProps {
  $col: Col
  $readOnly?: boolean
}

const hasContentState = () => css`
  ${getP18InputHasContentLabel()}
  opacity: 1;
  transform: translateY(-35px);
`

const optionsListVariants = {
  collapsed: {
    height: '0',
    opacity: 0,
    transitionEnd: {
      display: 'none',
    },
    transition: { ...getTransition() }
  },
  expanded: {
    height: 'auto',
    opacity: 1,
    display: 'block',
    transition: { ...getTransition() }
  }
}

const getOptionsList = (data, onClickOption, currentValue) => data.map((option, idx) => {
  return (
    <li key={`${option.value}-${idx}`} className={`${currentValue === option.value ? 'current' : ''}`}>
      <button type='button' onMouseDown={() => { onClickOption(option) }} data-dismiss={true} disabled={option.disabled}>
        {option.name ?? option}
      </button>
    </li>
  )
})

const arrowIcon = {
  type: 'svg' as const,
  alt: 'Arrow icon',
  src: '/images/svg/i--chevron--down--dark.svg',
  size: { width: 8, height: 12 }
}

export const Select = memo(({ options, name = 'country_code', defaultValue, label, placeholder, placeholderVisible = false, required = false, disabled = false, readOnly = null, onChange = null, col, register = noRegister, icon = null, className, textColor = 'white', bgColor = 'white', error, setFormValue, dropdownTextColor = null, modalId, reset = false, setReset, searchDisabled = false, $light = false}:iSelect):JSX.Element => {
  const requireProps: RegisterOptions = typeof required === 'boolean' ? { required } : required
  const { onChange:registerOnChange, onBlur:registerOnBlur, name:registerName, ref:registerRef } = register(name, { ...requireProps })
  const { isActive, payload } = useModal(modalId)

  const [timeoutId, setTimeoutId] = useState(null)
  const [timeoutIdSearch, setTimeoutIdSearch] = useState(null)

  const [triggerRef, dropdownRef, expanded, toggleExpanded, onTriggerFocus] = useDropdown()
  const [currentOptions, setCurrentOptions] = useState(options)
  const [isOptionClicked, setIsOptionClicked] = useState(false)

  const [fancyValue, setFancyValue] = useState(options.filter(option => option.value === defaultValue)[0]?.name ?? '')
  const [currentValue, setCurrentValue] = useState(defaultValue ?? '')
  const { isMobile } = useDeviceType()
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  const handleChange = e => {
    registerOnChange?.(e)
    onChange?.(e)
  }

  const handleBlur = e => {
    setIsFocused(false)
    registerOnBlur?.(e)
  }

  const onClickOption = (option) => {
    setIsOptionClicked(true)
    setFormValue?.(name, option.value)
    setCurrentValue(option.value)
    setFancyValue(option.value !== '' ? option.name : '')
    onChange?.(option.name)
    toggleExpanded()
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const Options = useMemo(() => getOptionsList(currentOptions, onClickOption, currentValue), [currentOptions, currentValue])

  const filterBySearch = useCallback(async ({ value, valid }) => {
    if (valid) {
      const data = options.filter(option => option.name.toLowerCase().includes(value.toLowerCase()))
      setCurrentOptions(data)
      if(!isFocused) {
        if (!data || data.length === 0) {
          setCurrentValue('')
          setFancyValue('')
          setFormValue?.(name, '')
        } else if(data?.length === 1) {
          const matchOption = data[0]
          setFormValue?.(name, matchOption?.value ?? '')
          setCurrentValue(matchOption?.value ?? '')
          setFancyValue(matchOption?.name ?? '')
        }
      }
    } else {
      setCurrentOptions(options)
    }
  }, [isFocused, name, options, setFormValue])

  const handleSearch = e => {
    if(searchDisabled) return
    const value = e?.target?.value
    setFancyValue(value)

    if(typeof value === 'string') {
      const valid = value.length > 1
      if(timeoutId) clearTimeout(timeoutId)
      setTimeoutId(setTimeout(() => filterBySearch({ value, valid }), 300))
    }
  }

  const onBlurSearch = useCallback(() => {
    if (isOptionClicked) {
      setIsOptionClicked(false)
      return
    }

    if(timeoutIdSearch) clearTimeout(timeoutIdSearch)
    setTimeoutIdSearch(setTimeout(() => {
      setIsOptionClicked(false)
      const value = fancyValue
      let matchOption = null
      if (typeof value === 'string' && value.length > 2) {
        matchOption = options.filter(option => option.name.toLowerCase().includes(value.toLowerCase()))
        if (matchOption.length === 1) matchOption = matchOption[0]
      }

      setFormValue?.(name, matchOption?.value ?? '')
      setCurrentValue(matchOption?.value ??  '')
      setFancyValue(matchOption?.name ?? '')
      // Remove filtered options
      setCurrentOptions(options)
    }, 200))
  }, [fancyValue, isOptionClicked, name, options, setFormValue, timeoutIdSearch])

  useEffect(() => {
    setCurrentOptions(options)
  }, [options])

  useEffect(() => {
    let matchOption = null

    if (defaultValue) {
      matchOption = options.filter(option => option.value === defaultValue)?.[0]

      if(!matchOption) return
      setFormValue?.(name, matchOption?.value ?? '')
      setCurrentValue(matchOption?.value ?? '')
      setFancyValue(matchOption?.name ?? '')
    }
  }, [defaultValue, options, name, setFormValue])

  useEffect(() => {
    // This useEffect will only run if the <Select /> is inside a modal
    if(!modalId && !isActive) return
    let matchOption = null

    matchOption = options.filter(option => option.name === payload?.product_name)?.[0]
    setFormValue?.(name, matchOption?.value ?? '')
    setCurrentValue(matchOption?.value ?? '')
    setFancyValue(matchOption?.name ?? '')
  }, [isActive, payload, options, name, setFormValue, modalId])


  useEffect(() => {
    if(!reset) return
    setFormValue?.(name, '')
    setCurrentValue('')
    setFancyValue('')
    setReset?.(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset])

  return (
    <Div ref={triggerRef} $col={col} $readOnly={readOnly}>
      <input type='hidden' name={registerName} required={typeof required === 'boolean' ? required : undefined} aria-invalid={!!error}
        {...registerRef ? { ref: registerRef } : {}}
        {...defaultValue ? { defaultValue } : {}}
        {...handleChange ? { onChange: handleChange } : {}}
        {...handleBlur ? { onBlur: handleBlur } : {}}
        autoComplete='off' aria-autocomplete='none'
      />
      <Label $hasContent={!!fancyValue} $placeholderVisible={placeholderVisible || currentValue ? true : false} $textColor={textColor} $bgColor={bgColor} {...className && { className }}>
        <input type='text' name={`${name}_searcher`} disabled={disabled} readOnly={readOnly} placeholder={placeholder ?? label} onChange={handleSearch} onFocus={() => { onTriggerFocus()
          setIsFocused(true)
        }} onBlur={onBlurSearch} value={fancyValue} autoComplete='off' aria-autocomplete='none' onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} />
        <Span className='hover' custom={isMobile} transition={transition} variants={variantsSpan} initial='inactive' animate={!disabled && (isHovered || isFocused ) ? 'active' : 'inactive'} />
        {label && <span>{label}</span>}
        {icon ? icon : <Img media={arrowIcon} fill={false} lazy={true} className='chevron'/>}
      </Label>
      {!readOnly && <Ul ref={dropdownRef} initial='collapsed' variants={optionsListVariants} animate={expanded ? 'expanded' : 'collapsed'} $textColor={textColor} $dropdownTextColor={dropdownTextColor} $light={$light}>
        {Options}
      </Ul>}
    </Div>
  )
})



const Div = styled.div<iDivProps>`
  padding: 0 ;
  position: relative;
  pointer-events: ${({ $readOnly }) => $readOnly ? 'none' : 'auto'};
  width: 100%;

  ${ mq.greaterThan('tablet') } {
    width: ${({ $col }) => typeof $col === 'number' ? `${100 * $col / 12}%` : $col };
  }

  ${mq.greaterThan('desktop-xs')} {
  }

  ${({ $readOnly }) => { if($readOnly) return css`
    .chevron {
      opacity: 0;
    }
  `}}

  input[type='hidden'] {
    &[aria-invalid='true'] {
      + label {
        input {
          border-color: #FF3945;
        }

        .hover {
          background-color: #FF3945;
        }
      }

    }
  }
`

const Ul = styled(motion.ul)<{$light: boolean, $textColor: TypeThemeColors, $dropdownTextColor: TypeThemeColors}>`
  background: ${({ $light }) => $light ? 'var(--color-background)' : 'var(--color-creme)'};
  box-shadow: 5px 10px 8px #80808038;
  margin-top: 1px;
  max-height: 135.5px;
  overflow-y: auto;
  padding: ${vw(10, 'mobile')} 0;
  position: absolute;
  top: 25px;
  width: calc(100% - 8.5px);
  z-index: 5;

  ${mq.greaterThan('tablet')} {
    max-height: 201.5px;
    padding: ${vw(10, 'desktop')};
    top: 30px;
    width: calc(100% - 25px);
  }

  li {
    margin-bottom: 4.25px;
  }

  button {
    ${getP18()}
    opacity: .4;
    cursor: pointer;
    width: 100%;
    transition: opacity 300ms ease;
    text-align: left;

    &:not(:last-of-type) {
      margin-bottom: ${vw(5, 'mobile')};
    }

    ${mq.greaterThan('tablet')} {
      &:not(:last-of-type) {
        margin-bottom: ${vw(5, 'desktop')};
      }
    }

    &:hover {
      opacity: 1;
    }

    &[disabled] {
      opacity: .1;
    }
  }
`

const Label = styled.label<iLabelProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;


  > input {
    ${getP18()}
    background-color: transparent;
    border-bottom: 1px solid;
    border-color:rgba(77, 77, 77, .4);
    color: var(--color-text);
    /* height: 60px; */
    overflow: hidden;
    padding-bottom: ${vw(9.5, 'mobile')};
    padding-right: ${vw(20, 'mobile')};
    text-overflow: ellipsis;
    transition: 300ms border-color ease-out;
    white-space: nowrap;
    width: 100%;

    ${({ $placeholderVisible }) => parsePlaceHolder(`
      color: var(--color-text);
      opacity: ${$placeholderVisible ? .4 : 0};
    `)}


    ${mq.greaterThan('tablet')} {
      padding-bottom: ${vw(10, 'desktop')};
      padding-right: 0;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-text-fill-color: var(--color-text);
      /* -webkit-box-shadow: 0 0 0 500px transparent inset;
      box-shadow: 0 0 0 100px transparent inset; */
      color: inherit;
    }

    ${({ $placeholderVisible }) => parsePlaceHolder(`
      opacity: ${$placeholderVisible ? .4 : 0};
      color: var(--color-text);
    `)}

    &:focus {
      border-color: var(--color-dark-blue);

      /* + span {
        ${hasContentState()}
      } */
    }

    &[readonly],
    &[disabled] {
      ~ figure,
      ~ picture {
        opacity: 0;
      }
    }

    &[readonly] {
      opacity: .5;
      pointer-events: none;
      user-select: none;

      + span {
        opacity: .5;
      }
    }

    &[disabled] {
      opacity: .3;
      pointer-events: none;
      user-select: none;

      + span {
        opacity: .3;
      }
    }
  }

  > span:not(.hover) {
    ${({ $placeholderVisible }) => $placeholderVisible ? hasContentState() : getP18InputLabel()}
    border-bottom: 1px solid rgba(0, 0, 0, 0);
    bottom: 12px;
    color: var(--color-black);
    opacity: .4;
    position: absolute;
    transform: translateY(-35px);
    transition: 300ms font-size ease, 300ms transform ease, 300ms opacity ease;
    white-space: nowrap;
  }

  figure, picture, img {
    &.chevron {
      /* height: 24px; */
      pointer-events: none;
      position: absolute;
      right: 8px;
      width: 12px;

      img,
      svg {
        height: 100%;
        object-fit: contain;
        transition: 300ms transform ease-out;
        width: 100%;
      }
    }
  }
`
